import { useState } from 'react'
import useSWRInfinite from 'swr/infinite'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { AnimatePresence } from 'framer-motion'
import {
  ImageIcon,
  UploadIcon,
  Loader2Icon,
  XIcon,
  CheckCircle,
  CloudUpload,
  Cloud,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTranslations } from 'next-intl'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

import type { Asset, AssetType } from '@/lib/types'
import { cn, uploadBlobToCdn } from '@/lib/utils'
import { isHttps } from '@/lib/editor/utils'
import { useProjectStore } from '@/hooks/use-project-store'
import { useFlyingAnimation } from '@/hooks/use-flying-animation'
import { ScrollArea } from '@/components/ui/scroll-area'
import { FlyingImageAnimation } from '@/components/common/flying-image-animation'
import {
  useUploadAssets,
  ALLOWED_UPLOAD_ASSETS_FORMATS,
  ALLOWED_UPLOAD_ASSETS_FORMATS_TEXT,
  ALLOWED_UPLOAD_IMAGE_FORMATS_TEXT,
  ALLOWED_UPLOAD_IMAGE_FORMATS,
  ALLOWED_UPLOAD_VIDEO_FORMATS,
  ALLOWED_UPLOAD_VIDEO_FORMATS_TEXT,
} from '@/hooks/use-upload-assets'
import { AssetThumbnail } from './common/asset-card'
import { useEditorStore } from '@/hooks/use-editor-store'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import { useCurrentUser } from '@/hooks/use-current-user'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
const PAGE_SIZE = 20

interface UserAsset {
  _id: string
  asset_url: string
  type: string
  create_time: string
}

const getKey = (pageIndex: number, previousPageData: UserAsset[] | null) => {
  if (previousPageData && !previousPageData.length) return null
  return `/api/me/assets?skip=${pageIndex * PAGE_SIZE}&limit=${PAGE_SIZE}`
}

export const UploadAssets = ({
  onSelect,
  videoMode = false,
  allowBoth = false,
}: {
  onSelect?: (e: React.MouseEvent, asset: any) => void
  videoMode?: boolean
  allowBoth?: boolean // Allow both image and video uploads
} = {}) => {
  const t = useTranslations('upload-assets')
  const e = useTranslations('editor')
  const [isDragging, setIsDragging] = useState(false)
  const [dragUrl, setDragUrl] = useState('')
  // const [uploadProgress, setUploadProgress] = useState(0)
  const {
    setIsToolboxOpen,
    addSelectedAsset,
    selectedAssets,
    removeSelectedAsset,
    setChatBarHighlighting,
    project,
  } = useProjectStore()
  const { data: user } = useCurrentUser()
  const {
    getIsEditorOn,
    getIsTargetImage,
    getIsTargetVideo,
    setTargetSrc,
    setTargetSrcWithType,
  } = useEditorStore()

  const {
    uploadAssets,
    isUploading,
    setIsUploading,
    uploadProgress,
    getAssetThumbnail,
  } = useUploadAssets()

  const chatTabPosition = {
    x: 28,
    y: window.innerHeight - 128,
  }

  const { selectedImageUrl, clickPosition, handleSelect, resetAnimation } =
    useFlyingAnimation()

  const { data, size, setSize, isLoading, mutate } = useSWRInfinite<
    UserAsset[]
  >(getKey, async (url) => {
    const res = await fetch(url)
    if (!res.ok) throw new Error('Failed to fetch')
    return res.json()
  })

  const isImageSelectedInEditor = getIsEditorOn() && getIsTargetImage()
  const isVideoSelectedInEditor = getIsEditorOn() && getIsTargetVideo()
  // Allow media type switching: video can be selected when image is selected and vice versa
  const isMediaSelectedInEditor =
    isImageSelectedInEditor || isVideoSelectedInEditor
  const allAssets = data?.flat() || []
  // Filter assets based on mode
  const assets = allowBoth
    ? allAssets // Show all assets when allowBoth is true
    : videoMode
      ? allAssets.filter((asset) => asset.type === 'video')
      : allAssets
  const isEmpty = data?.[0]?.length === 0
  const hasNextPage = !(
    isEmpty ||
    (data && data[data.length - 1]?.length < PAGE_SIZE)
  )

  const [loadMoreRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: () => setSize((prev) => prev + 1),
    disabled: false,
  })

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleAssetsUpload = async (files: File[]) => {
    await uploadAssets(files, {
      assetSource: 'upload',
      assetDir: 'user-assets',
    })

    mutate()
    toast.success(t('upload_success'))
    // add mixpanel event
    sendToBothAnalytics(
      MIXPANEL_CLIENT_EVENT_NAMES.UPLOAD_ASSETS,
      { project_id: project?._id.toString() },
      user?._id.toString() || '',
    )
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const files = Array.from(e.dataTransfer.files)
    // await handleUpload(files)
    await handleAssetsUpload(files)
  }

  const handleDelete = async (assetId: string, assetUrl: string) => {
    try {
      const res = await fetch(`/api/me/assets?id=${assetId}`, {
        method: 'DELETE',
      })
      if (!res.ok) throw new Error('Failed to delete')
      mutate()
      const assetData: Asset = {
        source: 'upload',
        type: 'image',
        link: assetUrl,
        thumbnail: assetUrl,
      }
      removeSelectedAsset(assetData)
      toast.success(t('delete_success'))
    } catch (error) {
      toast.error(t('delete_error'))
    }
  }

  const handleUrlSubmit = async (url: string) => {
    if (!isHttps(url)) return

    // Create asset from URL
    const assetData: Asset = {
      source: 'upload',
      type: 'image',
      link: url,
      thumbnail: url,
    }

    if (onSelect) {
      const fakeEvent = {
        target: null,
        currentTarget: null,
        preventDefault: () => {},
        stopPropagation: () => {},
      } as unknown as React.MouseEvent
      onSelect(fakeEvent, assetData)
    }

    setDragUrl('')
  }

  const handleAssetClick = (e: React.MouseEvent, asset: UserAsset) => {
    const assetData: Asset = {
      source: 'upload',
      type: asset.type as AssetType, // 图片、video、audio
      link: asset.asset_url,
      thumbnail: asset.asset_url,
    }

    // If onSelect prop is provided (modal usage), use it instead
    if (onSelect) {
      console.log('UploadAssets - calling onSelect with asset:', assetData)
      onSelect(e, assetData)
      return
    }

    // Original logic for non-modal usage - use setTargetSrcWithType to allow media type switching
    if (isMediaSelectedInEditor) {
      setTargetSrcWithType(
        asset.asset_url,
        asset.type as 'image' | 'video' | 'audio',
      )
      setIsToolboxOpen(false)
      return
    }

    const isSelected = selectedAssets.some(
      (a) => a.type === assetData.type && a.link === assetData.link,
    )

    if (isSelected) {
      removeSelectedAsset(assetData)
      return
    }

    handleSelect(e, asset.asset_url)
    addSelectedAsset(assetData)
    setChatBarHighlighting(true)

    setTimeout(() => {
      resetAnimation()
      setChatBarHighlighting(false)
    }, 500)
  }

  return (
    <>
      <div className="flex flex-col h-full no-scrollbar">
        {/* URL Entry Section */}
        <div className="shrink-0 flex items-center justify-center">
          <div className="flex gap-2 w-full">
            <Input
              value={dragUrl}
              onChange={(e) => setDragUrl(e.target.value)}
              placeholder={
                videoMode ? 'https://... (video URL)' : 'https://...'
              }
              className="flex-1 bg-[#212121] bg-white/10 rounded-[12px] text-white placeholder-white/50 h-10 text-sm"
            />
            <Button
              onClick={() => handleUrlSubmit(dragUrl)}
              disabled={!isHttps(dragUrl)}
              className="bg-white text-black hover:bg-gray-200 disabled:opacity-50 text-sm h-10 rounded-[12px]"
            >
              {e('use_url')}
            </Button>
          </div>
        </div>

        {/* Drag & Drop Upload Area */}
        <div className="shrink-0 py-5">
          <label className="block cursor-pointer max-w-full">
            <input
              type="file"
              className="hidden"
              accept={
                allowBoth
                  ? ALLOWED_UPLOAD_ASSETS_FORMATS.join(',')
                  : videoMode
                    ? ALLOWED_UPLOAD_VIDEO_FORMATS.join(',')
                    : ALLOWED_UPLOAD_IMAGE_FORMATS.join(',')
              }
              multiple
              disabled={isUploading}
              onChange={async (e) => {
                const files = Array.from(e.target.files || [])
                if (files.length === 0) return
                await handleAssetsUpload(files)
              }}
            />
            <div
              className={cn(
                'border-2 border-dashed rounded-xl p-4 text-center transition-colors relative w-full',
                isDragging
                  ? 'border-gold bg-gold/5'
                  : 'border-white/30 hover:border-gold hover:bg-gold/5',
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isUploading ? (
                <div className="flex flex-col items-center">
                  <div className="p-2 rounded-full mb-1">
                    <Loader2Icon className="w-5 h-5 text-gold animate-spin" />
                  </div>
                  <div className="mb-2">
                    <p className="text-sm font-medium text-white">
                      {t('uploading')}
                    </p>
                    <p className="text-xs text-white opacity-50 mt-1">
                      {Math.round(uploadProgress)}% {t('complete')}
                    </p>
                  </div>
                  <div className="w-full max-w-xs h-1 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gold transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <CloudUpload className="w-6 h-6 text-white opacity-50" />
                  <div>
                    <p className="text-sm text-white opacity-50">
                      {allowBoth
                        ? e('drag_drop_or_click_to_upload')
                        : videoMode
                          ? 'Drag & drop or click to upload videos'
                          : e('drag_drop_or_click_to_upload')}
                    </p>
                    {(videoMode || allowBoth) && (
                      <p className="text-xs text-white opacity-30 mt-1">
                        {allowBoth
                          ? ALLOWED_UPLOAD_ASSETS_FORMATS_TEXT
                          : ALLOWED_UPLOAD_VIDEO_FORMATS_TEXT}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </label>
        </div>

        {!isEmpty && (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="pb-2 flex-shrink-0">
              <h3 className="text-sm font-medium text-white mb-2">
                {t('uploaded_files')}
              </h3>
            </div>
            <div className="flex-1 pb-4 overflow-auto no-scrollbar">
              <div className="grid grid-cols-3 gap-3">
                {assets.map((asset) => (
                  <div
                    key={asset._id}
                    className="group relative aspect-square bg-gray-800 rounded-lg overflow-hidden object-cover
                    transition-transform hover:scale-[1.02]"
                  >
                    <button
                      type="button"
                      onClick={(e) => handleAssetClick(e, asset)}
                      className="w-full h-full"
                    >
                      <AssetThumbnail
                        asset={{
                          type: asset.type as AssetType,
                          link: asset.asset_url,
                          thumbnail: asset.asset_url,
                        }}
                        alt="Uploaded asset"
                        className="w-full h-full"
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(asset._id, asset.asset_url)}
                      className="absolute top-2 right-2 bg-white rounded-full p-0.5 shadow-sm hover:bg-white/80 opacity-0 group-hover:opacity-100"
                    >
                      <XIcon className="w-4 h-4 text-black" />
                    </button>
                    {selectedAssets.some((a) => a.link === asset.asset_url) && (
                      <div className="absolute top-2 left-2 bg-gold text-black rounded-full p-1">
                        <CheckCircle className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                ))}
                {hasNextPage && (
                  <div ref={loadMoreRef} className="col-span-3 h-8" />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        <FlyingImageAnimation
          isVisible={!!(selectedImageUrl && clickPosition)}
          sourcePosition={clickPosition || { x: 0, y: 0 }}
          targetPosition={chatTabPosition}
          imageUrl={selectedImageUrl || ''}
        />
      </AnimatePresence>
    </>
  )
}
